"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useFocus = void 0;
const react_1 = require("react");
const useFocus = (initialState, isChildElement) => {
    const [isFocused, setIsFocused] = (0, react_1.useState)(initialState);
    const getFocusedElement = (0, react_1.useCallback)(() => {
        let element = document.activeElement;
        while (element instanceof HTMLIFrameElement) {
            element = element.contentDocument?.activeElement || null;
        }
        return element?.parentElement ? element : null;
    }, []);
    const handleFocus = (0, react_1.useCallback)((event) => {
        // Ignore elements flagged to be ignored, this allows us to add extra, clickable, elements
        // without triggering a focus, such as action menus.
        if (!event.target?.classList?.contains('fte-ignore') && !event.target?.closest('.fte-ignore')) {
            setIsFocused(true);
        }
    }, []);
    const handleBlur = (0, react_1.useCallback)((event) => {
        // React event bubbling is interesting, it bubbles up the React tree rather than the DOM tree.
        // The tree deviates when rendering portals (eg. for modals).
        //
        // Only hide the toolbar if:
        // 1. We are blurring a node in the editor **DOM** tree.
        // 2. We are focusing on something that is not in the editor DOM tree
        //    (elements in the portal won't be in the tree but don't influence the focus state per #1).
        //
        // This avoids the scenario where an element in a portal is blurred and another one in the portal focused.
        // Without this logic the blur and focus handlers are called (in that order). The impact of these handlers being
        // called is that the "isFocused" state changes inconsistently. This state changing then causes subtle issues.
        // eg. unable to drill down in resource browser, toolbar appearing/disappearing.
        //
        // Ideally we would instead solely seeing if the "relatedTarget" is in the React tree. This isn't easily
        // identifiable however without reaching into React internals.
        //
        // An assumption here is that anything in a portal will only blur to another element that is also in the portal
        // (and therefore still in our React tree resulting in the element still effectively being focused).
        // TODO: PLATFORM-1611 this shit still doesn't work properly, notably issues with the link/image modals.
        requestAnimationFrame(() => {
            // "relatedTarget" in the event object will be null if the element gaining focus is in a different
            // document to the element being blurred (eg. floating toolbar rendered in top level frame,
            // editor rendered in iframe). instead grab the active element to determine current focus.
            const isBlurringEditor = isChildElement(event.target);
            const focusedElement = event.relatedTarget || getFocusedElement();
            const isFocusedInEditor = focusedElement && isChildElement(focusedElement);
            // Detect if the blur event happens when the related/clicked target is the floating popover
            const isClickingFloatingToolbar = !!focusedElement?.closest('.squiz-fte-scope__floating-popover');
            if (isBlurringEditor && !isFocusedInEditor && !isClickingFloatingToolbar) {
                setIsFocused(false);
            }
        });
    }, [getFocusedElement, isChildElement]);
    return { handleFocus, handleBlur, isFocused };
};
exports.useFocus = useFocus;
